import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import '../../styles/chat-theme.css'

interface ChatThread {
  id: string
  tenant_id: string
  client_id: string | null
  subject: string | null
  status: string
  ai_messages_count: number
  human_messages_count: number
  created_at: string
  updated_at: string
  client_name?: string
  site_name?: string
  last_message?: string
  last_message_at?: string
}

interface ChatMessage {
  id: string
  sender_type: 'client' | 'user' | 'ai' | 'system'
  sender_name: string | null
  content: string
  created_at: string
  is_internal: number
  kb_articles_referenced?: string | null
  confidence_score?: number | null
}

interface ChatStats {
  active_conversations: number
  resolved_today: number
  ai_resolution_rate: number
  avg_satisfaction: number | null
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'active':
    case 'waiting_on_ai':
      return 'active'
    case 'waiting_on_human':
      return 'waiting'
    case 'resolved_by_ai':
    case 'closed':
      return 'resolved'
    case 'escalated':
      return 'escalated'
    default:
      return 'active'
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'active': return 'AI handling'
    case 'waiting_on_ai': return 'AI processing'
    case 'waiting_on_human': return 'Waiting on human'
    case 'resolved_by_ai': return 'Resolved by AI'
    case 'escalated': return 'Escalated'
    case 'closed': return 'Closed'
    default: return status
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function ChatSupportDashboard() {
  const { toast, toastError } = useToast()
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [selectedThread, setSelectedThread] = useState<ChatThread | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [stats, setStats] = useState<ChatStats | null>(null)
  const [replyText, setReplyText] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadThreads = useCallback(async () => {
    try {
      const result = await api.get<{ data: ChatThread[] }>(
        `/chat/admin/threads${statusFilter ? `?status=${statusFilter}` : ''}`
      )
      setThreads(result.data || [])
    } catch (err) {
      console.error('[ChatDashboard] Failed to load threads', err)
    }
  }, [statusFilter])

  const loadStats = useCallback(async () => {
    try {
      const data = await api.get<ChatStats>('/chat/admin/stats')
      setStats(data)
    } catch {
      // Non-critical
    }
  }, [])

  const loadMessages = useCallback(async (threadId: string) => {
    try {
      const data = await api.get<{ messages: ChatMessage[] }>(`/chat/threads/${threadId}`)
      setMessages(data.messages || [])
    } catch (err) {
      toastError('Failed to load messages', err)
    }
  }, [toastError])

  // Initial load + polling
  useEffect(() => {
    setLoading(true)
    Promise.all([loadThreads(), loadStats()]).finally(() => setLoading(false))

    // Poll every 5 seconds
    pollRef.current = setInterval(() => {
      loadThreads()
      if (selectedThread) loadMessages(selectedThread.id)
    }, 5000)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [loadThreads, loadStats, selectedThread, loadMessages])

  // Refresh on filter change
  useEffect(() => {
    loadThreads()
  }, [statusFilter, loadThreads])

  const selectThread = async (thread: ChatThread) => {
    setSelectedThread(thread)
    setReplyText('')
    setIsInternal(false)
    await loadMessages(thread.id)
  }

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendReply = async () => {
    if (!selectedThread || !replyText.trim()) return

    setSending(true)
    try {
      await api.post(`/chat/threads/${selectedThread.id}/messages`, {
        content: replyText.trim(),
        sender_type: 'user',
        is_internal: isInternal,
      })
      setReplyText('')
      await loadMessages(selectedThread.id)
      await loadThreads()
    } catch (err) {
      toastError('Failed to send reply', err)
    } finally {
      setSending(false)
    }
  }

  const handleEscalate = async () => {
    if (!selectedThread) return
    try {
      const result = await api.post<{ ticket_number: number }>(`/chat/threads/${selectedThread.id}/escalate`)
      toast(`Escalated to Ticket #${result.ticket_number}`)
      await loadMessages(selectedThread.id)
      await loadThreads()
    } catch (err) {
      toastError('Failed to escalate', err)
    }
  }

  const handleResolve = async () => {
    if (!selectedThread) return
    try {
      await api.post(`/chat/threads/${selectedThread.id}/resolve`)
      toast('Conversation resolved')
      await loadMessages(selectedThread.id)
      await loadThreads()
      await loadStats()
    } catch (err) {
      toastError('Failed to resolve', err)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendReply()
    }
  }

  const formatTime = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div>
      {/* Stats bar */}
      {stats && (
        <div className="chat-stats-bar" style={{ marginBottom: 16, background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
          <div className="chat-stat">
            <div className="chat-stat-value">{stats.active_conversations}</div>
            <div className="chat-stat-label">Active</div>
          </div>
          <div className="chat-stat">
            <div className="chat-stat-value">{stats.resolved_today}</div>
            <div className="chat-stat-label">Resolved Today</div>
          </div>
          <div className="chat-stat">
            <div className="chat-stat-value">{stats.ai_resolution_rate}%</div>
            <div className="chat-stat-label">AI Resolution</div>
          </div>
          <div className="chat-stat">
            <div className="chat-stat-value">{stats.avg_satisfaction ?? '—'}</div>
            <div className="chat-stat-label">Avg Rating</div>
          </div>
        </div>
      )}

      <div className="chat-dashboard">
        {/* Thread List */}
        <div className="chat-dashboard-sidebar">
          <div className="chat-dashboard-sidebar-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>Conversations</span>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 6,
                  color: 'var(--text-primary)',
                  fontSize: 12,
                  padding: '4px 8px',
                }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="waiting_on_human">Waiting</option>
                <option value="escalated">Escalated</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="chat-dashboard-list">
            {loading && threads.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading...</div>
            ) : threads.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>No conversations</div>
            ) : (
              threads.map(t => (
                <div
                  key={t.id}
                  className={`chat-thread-item ${selectedThread?.id === t.id ? 'active' : ''}`}
                  onClick={() => selectThread(t)}
                >
                  <div className={`chat-thread-status-dot ${getStatusDotClass(t.status)}`} />
                  <div className="chat-thread-info">
                    <div className="chat-thread-name">
                      {t.client_name || t.subject || 'Conversation'}
                    </div>
                    <div className="chat-thread-preview">
                      {t.last_message || t.subject || 'No messages yet'}
                    </div>
                    <div className="chat-thread-meta">
                      {getStatusLabel(t.status)} &middot; {timeAgo(t.last_message_at || t.updated_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Thread Detail */}
        <div className="chat-dashboard-main">
          {!selectedThread ? (
            <div className="chat-dashboard-empty">
              Select a conversation to view
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div style={{
                padding: '12px 20px',
                borderBottom: '1px solid var(--border-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {selectedThread.client_name || selectedThread.subject || 'Conversation'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {selectedThread.site_name && <span>{selectedThread.site_name} &middot; </span>}
                    {selectedThread.ai_messages_count} AI msgs &middot; {selectedThread.human_messages_count} human msgs
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedThread.status !== 'escalated' && selectedThread.status !== 'closed' && (
                    <>
                      <Button
                        variant="secondary"
                        onClick={handleResolve}
                        size="sm"
                      >
                        Resolve
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleEscalate}
                        size="sm"
                      >
                        Escalate to Ticket
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="chat-widget-messages" style={{ flex: 1 }}>
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`chat-msg chat-msg--${msg.sender_type === 'ai' ? 'ai' : msg.sender_type === 'system' ? 'system' : 'user'}`}
                  >
                    <div className="chat-msg-bubble" style={msg.is_internal ? { borderLeft: '2px solid var(--warning)', opacity: 0.8 } : undefined}>
                      {msg.is_internal ? <span style={{ fontSize: 11, color: 'var(--warning)', display: 'block', marginBottom: 4 }}>Internal Note</span> : null}
                      {msg.content}
                    </div>
                    <div className="chat-msg-meta">
                      {msg.sender_name && <span>{msg.sender_name}</span>}
                      <span style={{ textTransform: 'capitalize' }}>{msg.sender_type}</span>
                      <span>{formatTime(msg.created_at)}</span>
                      {msg.confidence_score != null && (
                        <span style={{ color: msg.confidence_score >= 70 ? 'var(--success)' : 'var(--warning)' }}>
                          {msg.confidence_score}% conf
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              {selectedThread.status !== 'closed' && (
                <div className="chat-reply-box">
                  <div className="chat-reply-row">
                    <textarea
                      className="chat-reply-input"
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={isInternal ? 'Add internal note...' : 'Type a reply...'}
                      rows={1}
                      disabled={sending}
                    />
                    <button
                      className="chat-widget-send-btn"
                      onClick={handleSendReply}
                      disabled={!replyText.trim() || sending}
                      aria-label="Send"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    </button>
                  </div>
                  <div className="chat-reply-options">
                    <label className="chat-reply-internal-toggle">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={e => setIsInternal(e.target.checked)}
                      />
                      Internal note
                    </label>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
